
import { Component, Vue } from 'vue-property-decorator'
import { TixTime } from '@/TixTime/TixTime'
import store from '@/store/store'
import type { TTStringFormat } from '@/TixTime/helpers'
import { isCurrentOrPastMember, membershipIsRenewable, membershipRenewableFrom } from '@/state/Membership'
import { mapGetters } from 'vuex'
import { API } from '@/api/API'
import { environment } from '@/helpers/Environment'
import type { EventWithConfig, LinkedTG } from '@/api/types/processedEntities'
import { getMembershipConfigProperty } from '@/helpers/Config'
import { groupBy, indexBy, indexItemsById } from '@/helpers/IndexHelpers'
import { apiErrorMessageOrRethrow } from '@/errors/helpers'
import { getMembershipRules, type MembershipRules } from '@/api/Membership'

@Component({
  computed: mapGetters({
    membership: 'Member/membership',
    ticketTypes: 'Member/ticketTypes',
    membershipLevelName: 'Member/membershipLevelName',
    membershipId: 'Member/membershipId',
    membershipScanCode: 'Member/membershipScanCode',
    autoRenewal: 'Member/autoRenewal',
    ticketGroupWithConfig: 'Member/ticketGroupWithConfig',
    eventTemplateWithConfig: 'Member/eventTemplateWithConfig',
  }),
})
export default class MembershipDetailsRoute extends Vue {
  membershipId: string
  membershipScanCode: string
  membership?: MembershipEntity
  membershipLevelName: string
  autoRenewal: AutoRenewState
  ticketGroupWithConfig: LinkedTG
  eventTemplateWithConfig: EventWithConfig
  showEmailSentMessage: boolean = false
  loading: boolean = false
  ticketTypes: TicketType[]

  savedPaymentMethodsResponse: ApiResponse<'saved_payment_method'> | null = null
  walletPrecheckResponse: ApiResponse<'wallet_precheck'> | null = null
  membershipRules: MembershipRules | null = null

  created() {
    if (this.hasMembership) {
      this.loading = true
      Promise.all([this.initMembershipRules(), this.initSavedPaymentMethods()])
        .then(this.initWalletConfig)
        .finally(() => {
          this.loading = false
        })
    }
  }

  async initMembershipRules(): Promise<void> {
    const typesById: Dict<TicketType> = indexItemsById(store.getters['Member/ticketTypes'])
    this.membershipRules = await getMembershipRules({ 'ticket_type.id._in': Object.keys(typesById) })
  }

  async initSavedPaymentMethods(): Promise<void> {
    this.savedPaymentMethodsResponse = await API.getUncached<'saved_payment_method'>(
      'my/identity/saved_payment_method',
    )
  }

  async initWalletConfig(): Promise<void> {
    if (!this.primaryTicketId) {
      // Do not show the add-to-wallet buttons if there is no primary ticket, such as wrong seller.
      return
    }

    try {
      this.walletPrecheckResponse = await API.getUncached<'wallet_precheck'>(
        `ticket/${this.primaryTicketId}/walletprecheck`,
      )
    } catch (e: any) {
      // A 404 error is expected if no wallets are configured,
      // or if the user is viewing their membership under the wrong seller.
      apiErrorMessageOrRethrow(e, ['not_found'])
    }
  }

  /**
   * Updates the primary (member's) ticket ID by:
   *
   * 1. Retrieving all tickets from the Member store.
   * 2. Filtering tickets whose `ticket_type.party_role` is 'Member'.
   * 3. Using the filtered tickets if available; otherwise, falling back to all tickets.
   * 4. Sorting the resulting list by `ticket_type._rank` in ascending order.
   * 5. Using the first ticket from the sorted list, if any.
   *
   * TODO Simplify as per suggestion on GitHub.
   * @see https://github.com/stqry/tix-web-client/pull/2989#discussion_r2176126277
   */
  get primaryTicketId(): void | null | string {
    if (!this.membershipRules) {
      // TODO Remove this or document the scenarios when membership rules are not available.
      return null
    }

    const typesById: Dict<TicketType> = indexItemsById(store.getters['Member/ticketTypes'])
    const { rules } = this.membershipRules
    const rulesByTicketTypeId: Dict<MembershipRuleEntity[]> = groupBy(
      'ticket_type_id',
      rules.filter((r) => r.party_role === 'Member'),
    )
    const tickets: Ticket[] = store.getters['Member/tickets']
    const primaryMemberTickets = tickets.filter((ticket) => {
      return Boolean(rulesByTicketTypeId[ticket.ticket_type_id])
    })
    const sortedTickets = (primaryMemberTickets.length > 0 ? primaryMemberTickets : tickets)
      .map((ticket) => ({ ...ticket, rank: typesById[ticket.ticket_type_id]._rank }))
      .sort((a, b) => a.rank - b.rank)

    // Tickets may be empty, such as when viewing a membership from a different seller.
    return sortedTickets[0]?.id
  }

  get savedCard(): SavedPaymentMethod | undefined {
    return this.savedPaymentMethodsResponse!.saved_payment_method._data[0]
  }

  // There are several ambiguities regarding when to display the "Add to Apple/Google Wallet" buttons.
  // For now, we are choosing the simplest yet acceptable approach:
  // show the buttons if their corresponding wallets are configured even if the device doesn't support it.
  //
  // Alternatives we considered:
  //
  // 1. Detect the OS and browser using `navigator.userAgent`.
  //    This approach was rejected because relying on user agent strings is generally discouraged:
  //    they are inconsistent, easily spoofed, and prone to breaking with browser updates.
  //
  // 2. Always display the "Add to Google Wallet" button when Google Wallet is configured,
  //    and show the "Add to Apple Wallet" button only if `window.ApplePaySession` is present.
  //    While this is a reasonable proxy for Apple Wallet support, there are edge cases (e.g., iPhone 5s and earlier)
  //    where Apple Wallet is available but Apple Pay is not.
  //
  // Refer to [this discussion](https://github.com/stqry/tix-web-client/pull/2850#pullrequestreview-2731053870) for more details.
  get walletConfigs(): { apple: boolean; google: boolean } {
    if (!this.walletPrecheckResponse) {
      return { apple: false, google: false }
    }

    const walletPrecheck = indexBy('provider', this.walletPrecheckResponse.wallet_precheck._data)
    return {
      apple: walletPrecheck.apple.ok!,
      google: walletPrecheck.google.ok!,
    }
  }

  get renewAllowed() {
    return membershipIsRenewable()
  }

  get expiry(): string {
    const expiry: TixTime = store.getters['Member/membershipExpiry']
    const medium = this.$t('dateFormat.medium') as TTStringFormat
    const formattedExpiry = expiry.format(medium)
    const renewFrom = membershipRenewableFrom()?.format(medium)

    if (!this.renewAllowed && renewFrom) {
      return `${formattedExpiry} (renew from ${renewFrom})`
    } else {
      return formattedExpiry
    }
  }

  get cardIcon() {
    if (!this.savedCard) {
      return null
    }
    const identifier = this.savedCard.identifier
    const brand = identifier.toLowerCase().split('…')[0].replace(/\s/g, '')
    return `/assets/default/images/${brand}.svg`
  }

  get cancelAutoRenewRoute() {
    return `/manage/${this.ticketOrderId}/cancel-auto-renew`
  }

  get autoRenewEnabled() {
    const legacy = environment.config.ask_to_autorenew ? 'prompt' : undefined
    const ticketGroup = this.ticketGroupWithConfig
    const event = this.eventTemplateWithConfig
    const autorenew = getMembershipConfigProperty('renewal', 'automatic', ticketGroup, event, legacy) ?? null
    return autorenew === 'prompt'
  }

  resendMembershipEmail() {
    this.loading = true
    API.post('my/ticket_order/resend', { body: { memberships_only: true } })
      .then(() => {
        this.showEmailSentMessage = true
      })
      .finally(() => {
        this.loading = false
      })
  }

  get hasMembership(): boolean {
    return isCurrentOrPastMember()
  }

  get ticketOrderId(): string {
    return this.membership!.ticket_order_id
  }
}
